import { Link } from "react-router-dom";
import { TopBar, Marquee } from "@/components/Shell";
import { HOME } from "@/constants/testIds";
import {
  ArrowRight, ShieldCheck, FileCheck2, ClipboardList, Users, Zap, Globe2, Building2,
} from "lucide-react";

const PIPELINE_DEMO = [
  { name: "M. Okoro", ref: "MM-9A21", tag: "under_review", role: "Splicer · Fidium" },
  { name: "L. Fernández", ref: "MM-71C0", tag: "approved", role: "Drop bury · Frontier" },
  { name: "R. Sinha", ref: "MM-4E88", tag: "submitted", role: "FTTH install · Spectrum" },
  { name: "H. Al-Amin", ref: "MM-B315", tag: "under_review", role: "Splicer · KAO" },
];

const MARQUEE_ITEMS = [
  "FTTH installers",
  "Drop bury crews",
  "Splicing specialists",
  "Frontier / Verizon",
  "Fidium",
  "Omni Fiber",
  "Spectrum",
  "Brightspeed",
];

const FEATURES = [
  {
    key: "clients",
    icon: <Building2 size={22} />,
    title: "Per-client intakes",
    body: "Spin up a branded intake link per client (KAO ENTERPRISE, etc.) and every applicant is auto-tagged to the right project.",
  },
  {
    key: "roles",
    icon: <ClipboardList size={22} />,
    title: "Project + role capture",
    body: "Candidates self-select the project (Frontier, Fidium, Omni, Spectrum, Brightspeed…) and their field discipline.",
  },
  {
    key: "immigration",
    icon: <ShieldCheck size={22} />,
    title: "Immigration workflow",
    body: "Status + proofs uploaded, expiry tracked, and verified with reviewer notes.",
  },
  {
    key: "console",
    icon: <Zap size={22} />,
    title: "Admin + PM console",
    body: "Approve, reject, verify docs, and monitor pipeline health per client and per project.",
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <TopBar mode="public" />

      {/* HERO */}
      <section className="relative border-b-2 border-black overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 fade-up">
            <div className="flex items-center gap-2 mb-6">
              <span className="pill pill-ink">Magna Mentes Inc.</span>
              <span className="pill pill-flame">FTTH crews · nationwide</span>
            </div>
            <h1
              data-testid={HOME.heroTitle}
              className="font-display text-6xl md:text-7xl lg:text-8xl uppercase"
            >
              We source the <span className="text-[rgb(var(--flame))]">crews</span><br />
              that build the <span className="underline decoration-[6px] underline-offset-[10px]">network</span>.
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-black/80">
              Magna Mentes is a management &amp; recruitment company for FTTH
              build partners. We capture, vet, and mobilize field crews for
              Frontier, Verizon, Fidium, Omni Fiber, Spectrum, Brightspeed and
              other operators — for clients like <b>KAO ENTERPRISE LLC</b>.
            </p>

            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/apply"
                data-testid={HOME.startApplication}
                className="btn-brut btn-flame text-base"
              >
                Start onboarding <ArrowRight size={18} strokeWidth={2.5} />
              </Link>
              <Link
                to="/admin/login"
                data-testid={HOME.adminLogin}
                className="btn-brut"
              >
                Admin console
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <div className="pill"><Building2 size={12} /> Client intakes</div>
              <div className="pill"><ShieldCheck size={12} /> Immigration verified</div>
              <div className="pill"><FileCheck2 size={12} /> Docs & expiry</div>
              <div className="pill"><Users size={12} /> Admin + PM roles</div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
            <div className="brut-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em]">
                  Live pipeline · demo
                </div>
                <span className="pill pill-flame">4 active</span>
              </div>
              <ul className="divide-y-2 divide-black">
                {PIPELINE_DEMO.map((r) => (
                  <li key={r.ref} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{r.name}</div>
                      <div className="font-mono text-xs text-black/60">
                        {r.ref} · {r.role}
                      </div>
                    </div>
                    <span
                      className={`pill ${
                        r.tag === "approved" ? "pill-success"
                        : r.tag === "under_review" ? "pill-teal"
                        : "pill-flame"
                      }`}
                    >
                      {r.tag.replace("_", " ")}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="dashed-line my-4" />
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-widest">
                  Active client · KAO ENTERPRISE
                </span>
                <span className="font-display text-2xl">01</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-black/60 mb-2">
              How it works
            </div>
            <h2 className="font-display text-5xl md:text-6xl uppercase">
              One platform.<br />Every <span className="text-[rgb(var(--flame))]">client</span>.
            </h2>
            <p className="mt-6 text-black/75 max-w-md">
              Magna Mentes admins create a per-client intake link, send it to
              candidates, and route every applicant into the right project
              pipeline — with full immigration &amp; payroll capture.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {FEATURES.map((f) => (
              <div key={f.key} className="brut-card brut-card-hover p-6">
                <div className="w-10 h-10 border-2 border-black grid place-items-center bg-[rgb(var(--paper-2))] mb-4">
                  {f.icon}
                </div>
                <h3 className="font-display text-2xl">{f.title}</h3>
                <p className="mt-2 text-sm text-black/75">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t-2 border-black bg-[rgb(var(--ink))] text-[rgb(var(--paper))]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] opacity-60 mb-3">
              Ready when you are
            </div>
            <h2 className="font-display text-5xl md:text-6xl uppercase">
              Recruiting for<br />KAO ENTERPRISE LLC.
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-4">
            <Link to="/apply/kao-enterprise-llc" className="btn-brut btn-flame">
              Apply — KAO intake <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
            <Link
              to="/admin/login"
              className="btn-brut"
              style={{
                background: "transparent",
                color: "rgb(var(--paper))",
                boxShadow: "4px 4px 0 0 rgb(var(--flame))",
              }}
            >
              Admin console
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-black">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2">
            <Globe2 size={14} /> Magna Mentes Inc. · Field Recruitment OS
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-black/60">
            © {new Date().getFullYear()} Magna Mentes Inc.
          </div>
        </div>
      </footer>
    </div>
  );
}
